import { useEffect, useRef } from "react";

const VISIBLE_CLASS = "app-header-visible";
const REVEAL_COOLDOWN_MS = 500;
const SWIPE_THRESHOLD_PX = 12;

function canInnerElementScrollUp(target: EventTarget | null): boolean {
  for (
    let el = target instanceof Element ? target : null;
    el && el !== document.body && el !== document.documentElement;
    el = el.parentElement
  ) {
    const { overflowY } = getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollTop > 0) return true;
  }
  return false;
}

/**
 * The global header is hidden on load and on every tab switch. It reappears
 * only after the user scrolls away from the top and back (or makes an upward
 * gesture on a page too short to scroll), and hides again when scrolling down.
 * The header stays in normal document flow, so it never overlaps content.
 */
export function useAutoHideHeader(resetKey: unknown) {
  const headerRef = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const hiddenAt = useRef(0);

  useEffect(() => {
    headerRef.current?.classList.remove(VISIBLE_CLASS);
    armed.current = false;
    hiddenAt.current = performance.now();
  }, [resetKey]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const isVisible = () => header.classList.contains(VISIBLE_CLASS);
    const hide = () => {
      if (isVisible()) hiddenAt.current = performance.now();
      header.classList.remove(VISIBLE_CLASS);
    };
    const show = () => header.classList.add(VISIBLE_CLASS);

    const onScroll = () => {
      if (window.scrollY > 0) {
        armed.current = true;
        if (isVisible() && window.scrollY >= header.offsetHeight) {
          // Hide only once fully scrolled out, and compensate scrollY so content does not jump.
          const height = header.offsetHeight;
          const y = window.scrollY;
          hide();
          window.scrollTo({ top: Math.max(0, y - height), behavior: "instant" });
        }
      } else if (
        armed.current &&
        !isVisible() &&
        performance.now() - hiddenAt.current > REVEAL_COOLDOWN_MS
      ) {
        show();
      }
    };
    const revealOnUpGesture = (target: EventTarget | null) => {
      if (window.scrollY > 0 || canInnerElementScrollUp(target)) return;
      armed.current = true;
      show();
    };
    const hideOnDownGesture = () => {
      if (window.scrollY !== 0 || !isVisible()) return;
      if (document.documentElement.scrollHeight - header.offsetHeight <= window.innerHeight + 1)
        hide();
    };
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) revealOnUpGesture(event.target);
      else if (event.deltaY > 0) hideOnDownGesture();
    };
    let touchStartY: number | null = null;
    const onTouchStart = (event: TouchEvent) => {
      touchStartY = event.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY;
      if (touchStartY === null || y === undefined) return;
      if (y - touchStartY > SWIPE_THRESHOLD_PX) {
        revealOnUpGesture(event.target);
        touchStartY = y;
      } else if (y - touchStartY < -SWIPE_THRESHOLD_PX) {
        hideOnDownGesture();
        touchStartY = y;
      }
    };
    const onTouchEnd = () => {
      touchStartY = null;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable))
        return;
      if (["ArrowUp", "PageUp", "Home"].includes(event.key)) revealOnUpGesture(target);
      else if (["ArrowDown", "PageDown", "End", " "].includes(event.key)) hideOnDownGesture();
    };

    const passive = { passive: true } as const;
    window.addEventListener("scroll", onScroll, passive);
    window.addEventListener("wheel", onWheel, passive);
    window.addEventListener("touchstart", onTouchStart, passive);
    window.addEventListener("touchmove", onTouchMove, passive);
    window.addEventListener("touchend", onTouchEnd, passive);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return headerRef;
}
